/**
 * `useFeedHold` — the React half of {@link ./feedHold}: keep a roll's COMMS card off the screen
 * until that roll's own dice have stopped rolling.
 *
 * Mounted by COMMS.app, which filters the held ids out at its render seam. Everything about
 * *which* entries hold is in the pure selector; this owns only the state and the subscription.
 *
 * The hold is derived **during render**, never in an effect. A passive effect runs after the
 * browser has painted, so a hold computed there lets a new card paint for one frame before it is
 * withheld — a flash a player can catch, and a race an e2e reading the feed from a single
 * non-retrying `evaluate` can lose (`comms-roll-card-one-frame-flash`). This uses React's "adjust
 * state when a prop changes" pattern instead: setting state during render makes React discard that
 * render's output and re-render before committing, so no committed frame ever shows the card. Only
 * the release — subscribing to each hold's settle — is an effect.
 *
 * The high-water mark is seeded from `feedBaselineTs` — the welcome backfill's own max `ts` —
 * never from this hook's first observation of the feed, for the reason `DiceFeedSubscriber`
 * records at length: the backfill is delivered asynchronously AFTER mount, so a self-seeded
 * baseline sees an empty array, records nothing, and then reads the entire backfill as new. Here
 * that bug would hold every backfilled result card on every page load.
 */
import { useEffect, useState } from "react";
import type { FeedEntry } from "@lancer-vtt/shared";
import { selectFeedHolds, type FeedHold } from "./feedHold";
import { awaitResolutionThrow, hasResolutionThrowSource } from "./resolutionThrows";

const NONE: ReadonlySet<string> = new Set();
const NO_HOLDS: readonly FeedHold[] = [];

/** Everything the hold remembers between renders, advanced together as one state value. */
type HoldTracker = {
  /** The feed last selected over — a new reference is what triggers a selection. */
  feed: readonly FeedEntry[] | null;
  baseline: number;
  highWater: number;
  held: ReadonlySet<string>;
  /** Entries already routed to a hold — a re-render over the same feed must not re-hold them. */
  claimed: ReadonlySet<string>;
  /** The holds the latest selection added; the release effect subscribes to exactly these. */
  fresh: readonly FeedHold[];
};

/** The ids of feed entries COMMS.app must not render yet. Empty whenever nothing is airborne. */
export function useFeedHold(feed: readonly FeedEntry[], feedBaselineTs: number): ReadonlySet<string> {
  const [tracker, setTracker] = useState<HoldTracker>(() => ({
    feed: null,
    baseline: feedBaselineTs,
    highWater: feedBaselineTs,
    held: NONE,
    claimed: NONE,
    fresh: NO_HOLDS,
  }));

  let current = tracker;
  if (tracker.feed !== feed || tracker.baseline !== feedBaselineTs) {
    // Raise (never lower) the baseline on a fresh join or a mid-session reconnect, so a re-delivered
    // tail is history rather than a flurry of new holds.
    const raised = Math.max(tracker.highWater, feedBaselineTs);
    const { holds, nextHighWater } =
      tracker.feed === feed ? { holds: [], nextHighWater: raised } : selectFeedHolds(feed, raised, hasResolutionThrowSource());
    const fresh = holds.filter((h) => !tracker.claimed.has(h.entryId));
    const freshIds = fresh.map((h) => h.entryId);
    current = {
      feed,
      baseline: feedBaselineTs,
      highWater: nextHighWater,
      held: fresh.length === 0 ? tracker.held : new Set([...tracker.held, ...freshIds]),
      claimed: fresh.length === 0 ? tracker.claimed : new Set([...tracker.claimed, ...freshIds]),
      fresh: fresh.length === 0 ? tracker.fresh : fresh,
    };
    setTracker(current);
  }

  const { fresh } = current;
  useEffect(() => {
    for (const hold of fresh) {
      // Every path through `awaitResolutionThrow` resolves — on the real settle, or on its own
      // ceiling — so a card held here is never left withheld. The release is idempotent, so a
      // StrictMode double-subscribe is harmless.
      void awaitResolutionThrow(hold.throwKey).then(() => {
        setTracker((prev) => {
          if (!prev.held.has(hold.entryId)) return prev;
          const next = new Set(prev.held);
          next.delete(hold.entryId);
          return { ...prev, held: next.size === 0 ? NONE : next };
        });
      });
    }
  }, [fresh]);

  return current.held;
}
